/**
 * Files, kept locally.
 *
 * The rule the whole file follows is the one on `Attachment` itself: **the document never carries
 * bytes.** A node holds attachment ids, an attachment holds a storage key, and the bytes live in
 * their own table under a key derived from their content. That is what keeps a map with a
 * repeated logo from costing its size twice, and what keeps a document small enough to diff,
 * sync and export.
 *
 * Two writes, one transaction. An attachment row pointing at bytes that are not there is a broken
 * image the user cannot explain; bytes with no row are invisible rubbish that never gets
 * collected. Neither is a state worth being able to reach.
 *
 * What this does *not* do is delete. Detaching a file is an undoable command, so the bytes have
 * to outlive it — an undo that restored a link to a file that had been deleted would restore a
 * broken image. Collecting genuinely unreferenced files is a sweep, and it belongs with the
 * cleanup queue the architecture already names.
 */
import { createAttachment } from '@orbit-mind/domain';
import { appError, err, ok, } from '@orbit-mind/shared-types';
import { attachmentSchema, parseAt } from '@orbit-mind/validators';
import { StorageError, storageFailure } from '../errors';
import { createDatabase } from './database';
/**
 * The largest file this will take.
 *
 * IndexedDB has no hard limit of its own, but a browser's quota is shared with everything else
 * the origin stores — including the journal that is the user's actual work. A refusal with a
 * number in it is kinder than a quota error halfway through a write.
 */
export const MAX_LOCAL_ATTACHMENT_BYTES = 25 * 1024 * 1024;
/**
 * A key that is the file's content.
 *
 * Two nodes given the same image produce the same key, so the second one writes over identical
 * bytes instead of a second copy. Scoped to the map because that is the unit the local library
 * deletes: purging a map takes its assets with it, and a key shared across maps would either
 * leave rubbish behind or take a file another map was using.
 */
export function localStorageKey(mapId, checksum) {
    return `local/${mapId}/${checksum}`;
}
/**
 * SHA-256 of a blob, as lowercase hex.
 *
 * WebCrypto, which every browser this ships to has. It is here rather than behind a port because
 * this file is already a browser adapter — the packages above it never see a byte.
 */
export async function checksumOf(file) {
    const buffer = await file.arrayBuffer();
    const digest = await crypto.subtle.digest('SHA-256', buffer);
    return [...new Uint8Array(digest)]
        .map((byte) => byte.toString(16).padStart(2, '0'))
        .join('');
}
/** Verify outside IndexedDB; crypto and Blob reads must finish before an outer restore transaction. */
export async function stageAttachmentRestore(attachment, bytes) {
    attachmentSchema.parse(attachment);
    const file = new Blob([new Uint8Array(bytes)], { type: attachment.mimeType });
    if (file.size > MAX_LOCAL_ATTACHMENT_BYTES ||
        file.size !== attachment.size ||
        (await checksumOf(file)) !== attachment.checksum)
        throw new StorageError('CHECKSUM_MISMATCH', 'The downloaded file failed verification', {
            retryable: true,
        });
    return { attachment, file };
}
/** Called only with staged data; contains exclusively IndexedDB awaits, joining an outer transaction. */
export async function writeStagedAttachment(db, staged) {
    const { attachment, file } = staged;
    await db.transaction('rw', db.attachments, db.assets, async () => {
        await db.assets.put({
            storageKey: attachment.storageKey,
            mapId: attachment.mapId,
            bytes: file,
            contentType: attachment.mimeType,
            createdAt: attachment.createdAt,
        });
        await db.attachments.put({
            attachmentId: attachment.id,
            mapId: attachment.mapId,
            nodeId: attachment.nodeId,
            storageKey: attachment.storageKey,
            attachment,
        });
    });
}
export function createLocalAttachmentStore(db = createDatabase()) {
    async function guarded(operation, body) {
        try {
            return ok(await body());
        }
        catch (cause) {
            return err(storageFailure(operation, cause));
        }
    }
    function read(row) {
        const parsed = parseAt(attachmentSchema, row.attachment, 'storage', 'attachment');
        if (!parsed.ok) {
            const first = parsed.error.issues[0];
            return {
                ok: false,
                reason: first === undefined ? parsed.error.code : `${first.path}: ${first.message}`,
            };
        }
        // Checked, then described — the same cast as everywhere else a row is read back.
        return { ok: true, value: parsed.value };
    }
    return {
        receive(attachment, bytes) {
            return guarded('receive attachment', async () => {
                const staged = await stageAttachmentRestore(attachment, bytes);
                await writeStagedAttachment(db, staged);
            });
        },
        add(input) {
            if (input.file.size > MAX_LOCAL_ATTACHMENT_BYTES) {
                return Promise.resolve(err(appError('PAYLOAD_TOO_LARGE', 'this file is too large to keep in the browser', {
                    details: { size: input.file.size, limit: MAX_LOCAL_ATTACHMENT_BYTES },
                    retryable: false,
                })));
            }
            return guarded('add attachment', async () => {
                const checksum = await checksumOf(input.file);
                const storageKey = localStorageKey(input.mapId, checksum);
                const attachment = createAttachment({
                    id: input.attachmentId,
                    workspaceId: input.workspaceId,
                    mapId: input.mapId,
                    nodeId: input.nodeId,
                    filename: input.filename,
                    mimeType: input.file.type === '' ? 'application/octet-stream' : input.file.type,
                    size: input.file.size,
                    storageKey,
                    checksum,
                    now: input.now,
                    by: input.by,
                });
                // One transaction. A row pointing at bytes that are not there is a broken image nobody
                // can explain, and bytes with no row are rubbish nothing will ever collect.
                await db.transaction('rw', db.attachments, db.assets, async () => {
                    await db.assets.put({
                        storageKey,
                        mapId: input.mapId,
                        bytes: input.file,
                        contentType: attachment.mimeType,
                        createdAt: input.now,
                    });
                    await db.attachments.put({
                        attachmentId: attachment.id,
                        mapId: attachment.mapId,
                        nodeId: attachment.nodeId,
                        storageKey,
                        attachment,
                    });
                });
                return attachment;
            });
        },
        get(attachmentId) {
            return guarded('get attachment', async () => {
                const row = await db.attachments.get(attachmentId);
                if (row === undefined)
                    return undefined;
                const parsed = read(row);
                if (parsed.ok)
                    return parsed.value;
                throw new StorageError('MAP_CORRUPTED', 'the attachment could not be read back', {
                    details: { attachmentId, reason: parsed.reason },
                    retryable: false,
                });
            });
        },
        listForMap(mapId) {
            return guarded('list attachments', async () => {
                const rows = await db.attachments.where('mapId').equals(mapId).toArray();
                const attachments = [];
                const damaged = [];
                for (const row of rows) {
                    const parsed = read(row);
                    // Reported rather than skipped: a file that silently stops appearing is
                    // indistinguishable, to the person who attached it, from one that was deleted.
                    if (parsed.ok)
                        attachments.push(parsed.value);
                    else
                        damaged.push({ sequence: null, reason: parsed.reason });
                }
                return { attachments, damaged };
            });
        },
        bytesOf(storageKey) {
            return guarded('read attachment bytes', async () => {
                const row = await db.assets.get(storageKey);
                return row?.bytes;
            });
        },
    };
}
//# sourceMappingURL=attachments.js.map